import { AccountsService } from 'base/accounts_service';
import { Button } from 'base/ui/button';
import { Checkbox } from 'base/ui/checkbox';
import { InvisibleForm, RedErrorText, useSubmit } from 'base/ui/form';
import { FormInput } from 'base/ui/input';
import { Inline, Rows, SpacerGrow } from 'base/ui/layout';
import { Link } from 'base/ui/link';
import { menuItem } from 'base/ui/menu_model';
import { DividerLine, DividerLineWithText } from 'base/ui/panel';
import { SelectButton } from 'base/ui/select';
import { ToastQueue } from 'base/ui/toast';
import {
  TextRegularLight,
  TextSmallLight,
  TextTinyLight,
  Title1,
  Title5,
} from 'base/ui/typography';
import { InviteTeamForm } from 'blocks/manage_account/invite_team_form';
import { TwoColumn } from 'blocks/manage_account/ui';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { useEffect } from 'react';
import styles from './forms.css';
import { SharedFormState } from './form_state';
import { GoogleOAuthButton } from './google_sign_in_gsi';
// Create a new combined form that handles both states
export const SignupForm = observer(function _SignupForm({
  hideTitle,
  formState,
  goToLogin,
  submitSignupForm,
}: {
  hideTitle?: boolean;
  formState: SharedFormState;
  goToLogin(): void;
  submitSignupForm(): Promise<void>;
}) {
  // State to track whether we're showing the full form or just email
  const [showFullForm, setShowFullForm] = React.useState(false);
  const [loading, error, handleSubmit] = useSubmit(submitSignupForm);

  const handleContinueWithEmail = () => {
    setShowFullForm(true);
  };

  return (
    <div className={styles.height}>
      <Rows spacing={24}>
        {!hideTitle && <Title1>Sign up</Title1>}

        <InvisibleForm
          onSubmit={showFullForm ? handleSubmit : handleContinueWithEmail}
          loading={loading}
        >
          {showFullForm && (
            <div className={styles.fadeInName}>
              <Rows spacing={16}>
                <FirstAndLastName formState={formState} autoFocus={true} />
              </Rows>
            </div>
          )}

          <div className={showFullForm ? styles.slideDown : ''}>
            <Rows spacing={16}>
              <Email formState={formState} blockPlusAddress />
            </Rows>
          </div>

          {showFullForm && (
            <div className={styles.fadeInPassword}>
              <Rows spacing={16}>
                <Password formState={formState} />
              </Rows>
            </div>
          )}

          <div className={showFullForm ? styles.slideDown : ''}>
            <Rows spacing={16}>
              <Button
                variant="primary"
                label={
                  showFullForm ? 'Create an account' : 'Continue with email'
                }
                loading={loading}
                type="submit"
                size="large"
              />
            </Rows>
          </div>

          <RedErrorText error={error} />
        </InvisibleForm>

        {!showFullForm && (
          <>
            <DividerLineWithText text={'or'} TextComponent={TextRegularLight} />
            <GoogleOAuthButton text="Continue with Google" />
            <SignupLegal />
          </>
        )}

        <DividerLine />
        <Inline yalign="center" spacing={8}>
          <TextRegularLight>Already have an account?</TextRegularLight>
          <SpacerGrow />
          <Button label="Log in" onClick={goToLogin} size="large" />
        </Inline>
      </Rows>
    </div>
  );
});

const SIGNUP_SURVEY_FIELDS = [
  {
    name: 'what-best-describes-you',
    label: 'What best describes you?',
    options: [
      'Freelancer',
      'Agency Owner',
      'Founder / Business Owner',
      'Marketer',
      'Designer',
      'Developer',
      'Copywriter',
      'Other',
    ],
  },
  {
    name: 'company-size',
    label: 'How many people work at your company?',
    options: ['Only Me', '2-10', '11-20', '21-50', '51-200', '200+'],
  },
  {
    name: 'company-type',
    label: 'What type of company is it?',
    options: [
      'Web Design Agency',
      'Marketing Agency',
      'Startup',
      'Mid-size Company',
      'Enterprise',
      'Other',
    ],
  },
  {
    name: 'platforms-used',
    label: 'What platforms are you using with Relume?',
    options: ['Figma', 'Webflow', 'React', 'Wordpress', 'Framer', 'Other'],
  },
  {
    name: 'how-did-you-hear-about-us',
    label: 'How did you hear about us?',
    options: [
      'Friend/Colleague',
      'Google/Search Engine',
      'YouTube',
      'Instagram/Facebook',
      'Twitter/X',
      'LinkedIn',
      'TikTok',
      'Product Hunt',
      'Webflow',
      'Figma',
      'ChatGPT',
      "I'm not sure",
      'Other',
    ],
  },
];

const FOLLOW_UP_CHANNELS = ['YouTube', 'Instagram/Facebook', 'TikTok'];
const FOLLOW_UP_FIELD = {
  name: 'how-did-you-hear-about-us-followup',
  options: ['Relume (official content)', 'Influencer (creator content)', 'Ads'],
};
const OTHER_FIELD_NAME = 'how-did-you-hear-about-us-other';

export function SignupSurveyForm({
  submitSignupSurveyForm,
}: {
  submitSignupSurveyForm(data: Record<string, string>): Promise<void>;
}) {
  const [data, setData] = React.useState<Record<string, string>>({});
  const [loading, error, handleSubmit] = useSubmit(() =>
    submitSignupSurveyForm(data),
  );
  const howDidYouHear = data['how-did-you-hear-about-us'];
  const requiresFollowUp = FOLLOW_UP_CHANNELS.includes(howDidYouHear || '');
  const requiresOther = howDidYouHear === 'Other';

  // Calculate how many fields must be answered
  let requiredFieldsCount = SIGNUP_SURVEY_FIELDS.length;
  if (requiresFollowUp) requiredFieldsCount += 1;
  if (requiresOther) requiredFieldsCount += 1; // The text input for 'Other'

  // Count how many are actually answered
  const answeredCount = Object.keys(data).filter((key) => {
    const val = data[key]?.trim();
    return val && val.length > 0;
  }).length;

  const submitDisabled = answeredCount < requiredFieldsCount;

  // Handle clearing and resetting fields based on selection changes
  React.useEffect(() => {
    setData((prevData) => {
      const newData = { ...prevData };
      // Handle follow-up field
      if (!requiresFollowUp && FOLLOW_UP_FIELD.name in newData) {
        delete newData[FOLLOW_UP_FIELD.name];
      }
      // Handle "Other" field
      if (!requiresOther && OTHER_FIELD_NAME in newData) {
        delete newData[OTHER_FIELD_NAME];
      }
      return newData;
    });
  }, [requiresFollowUp, requiresOther]);

  return (
    <Rows spacing={32}>
      <Title1>Tell us about yourself</Title1>
      <InvisibleForm onSubmit={handleSubmit} loading={loading}>
        {SIGNUP_SURVEY_FIELDS.map((field) => (
          <Rows spacing={8} key={field.name}>
            <TextSmallLight color="grey">{field.label}</TextSmallLight>
            {field.name === 'platforms-used' ? (
              <TwoColumn spacing={8}>
                {field.options.map((option) => (
                  <Checkbox
                    key={option}
                    label={option}
                    value={data[field.name]?.includes(option)}
                    onChange={(isChecked) => {
                      const updatedOptions = data[field.name]
                        ? data[field.name].split(', ')
                        : [];
                      if (isChecked) {
                        updatedOptions.push(option);
                      } else {
                        const index = updatedOptions.indexOf(option);
                        if (index > -1) {
                          updatedOptions.splice(index, 1);
                        }
                      }
                      setData({
                        ...data,
                        [field.name]: updatedOptions.join(', '),
                      });
                    }}
                    size="large"
                    labelColor="grey"
                  />
                ))}
              </TwoColumn>
            ) : (
              <SelectButton
                size="large"
                defaultLabel="Select response"
                disabled={loading}
                menu={field.options.map((option) =>
                  menuItem({
                    label: option,
                    onClick: () => setData({ ...data, [field.name]: option }),
                    checked: data[field.name] === option,
                  }),
                )}
              />
            )}
          </Rows>
        ))}

        {/* Conditional Follow-Up Question */}
        {requiresFollowUp && (
          <Rows spacing={8}>
            <TextSmallLight color="grey">
              What type of {howDidYouHear} content led you here?
            </TextSmallLight>
            <SelectButton
              size="large"
              defaultLabel="Select response"
              disabled={loading}
              menu={FOLLOW_UP_FIELD.options.map((option) =>
                menuItem({
                  label: option,
                  onClick: () =>
                    setData({ ...data, [FOLLOW_UP_FIELD.name]: option }),
                  checked: data[FOLLOW_UP_FIELD.name] === option,
                }),
              )}
            />
          </Rows>
        )}
        {/* Conditional "Other" text input */}
        {requiresOther && (
          <Rows spacing={8}>
            <TextSmallLight color="grey">
              Please specify how you heard about us:
            </TextSmallLight>
            <FormInput
              size="large"
              type="text"
              required
              value={data[OTHER_FIELD_NAME] || ''}
              onChange={(val) => setData({ ...data, [OTHER_FIELD_NAME]: val })}
              placeholder="Type your answer here"
            />
          </Rows>
        )}
        <Button
          variant="primary"
          label="Get started"
          loading={loading}
          disabled={submitDisabled}
          tooltip={submitDisabled ? 'Please answer all questions' : undefined}
          type="submit"
          size="large"
        />
        <RedErrorText error={error} />
      </InvisibleForm>
    </Rows>
  );
}

export const LoginForm = observer(function _LoginForm({
  hideTitle,
  formState,
  goToSignup,
  goToForgotPassword,
  submitLoginForm,
}: {
  hideTitle?: boolean;
  formState: SharedFormState;
  goToSignup(): void;
  goToForgotPassword(): void;
  submitLoginForm(): Promise<void>;
}) {
  const [loading, error, handleSubmit] = useSubmit(submitLoginForm);

  return (
    <Rows spacing={24}>
      {!hideTitle && <Title1>Log in</Title1>}
      <InvisibleForm onSubmit={handleSubmit} loading={loading}>
        <Rows spacing={16}>
          <Email formState={formState} />
          <Password
            formState={formState}
            goToForgotPassword={goToForgotPassword}
          />
        </Rows>
        <Button
          variant="primary"
          label="Log in"
          loading={loading}
          type="submit"
          size="large"
        />
        <RedErrorText error={error} />
      </InvisibleForm>
      <DividerLineWithText text={'or'} TextComponent={TextRegularLight} />
      <GoogleOAuthButton text="Login with Google" />
      <DividerLine />
      <Inline yalign="center" spacing={8}>
        <TextSmallLight>Don't have an account?</TextSmallLight>
        <SpacerGrow />
        <Button label="Sign up for free" onClick={goToSignup} size="large" />
      </Inline>
    </Rows>
  );
});

const FirstAndLastName = observer(function _FirstAndLastName({
  formState,
  autoFocus = false,
}: {
  formState: SharedFormState;
  autoFocus?: boolean;
}) {
  return (
    <div className={styles.nameColumns}>
      <Rows spacing={8}>
        <TextSmallLight color="grey">First name</TextSmallLight>
        <FormInput
          size="large"
          type="text"
          required
          value={formState.firstName}
          onChange={(v) => (formState.firstName = v)}
          autoComplete="given-name"
          autoFocus={autoFocus}
        />
      </Rows>
      <Rows spacing={8}>
        <TextSmallLight color="grey">Last name</TextSmallLight>
        <FormInput
          size="large"
          type="text"
          required
          value={formState.lastName}
          onChange={(v) => (formState.lastName = v)}
          autoComplete="family-name"
        />
      </Rows>
    </div>
  );
});

const Email = observer(function _Email({
  formState,
  blockPlusAddress,
}: {
  formState: SharedFormState;
  blockPlusAddress?: boolean;
}) {
  return (
    <Rows spacing={8}>
      <TextSmallLight color="grey">Email</TextSmallLight>
      <FormInput
        size="large"
        type="email"
        autoComplete="email"
        pattern={
          // we allow + for @relume.io emails so we can test things
          (blockPlusAddress && !formState.email.endsWith('@relume.io')
            ? /[^@+]+@[^@]+\.[^@]+/
            : /[^@]+@[^@]+\.[^@]+/
          ).source
        }
        required
        value={formState.email}
        onChange={(v) => (formState.email = v)}
        placeholder="user@company.com"
      />
    </Rows>
  );
});

const Password = observer(function _Password({
  formState,
  goToForgotPassword,
}: {
  formState: SharedFormState;
  goToForgotPassword?(): void;
}) {
  return (
    <Rows spacing={8}>
      <TextSmallLight color="grey">
        <Inline yalign="center" spacing={8}>
          Password
          <SpacerGrow />
          {goToForgotPassword && (
            <Link onClick={goToForgotPassword}>Forgot password?</Link>
          )}
        </Inline>
      </TextSmallLight>
      <FormInput
        size="large"
        type="password"
        autoComplete="current-password"
        required
        value={formState.password}
        onChange={(v) => (formState.password = v)}
        placeholder="Password"
      />
    </Rows>
  );
});

export function VerifyEmailForm({
  hideTitle,
  email,
  handleVerify,
  resendCode,
}: {
  hideTitle?: boolean;
  email: string;
  handleVerify(code: string): Promise<void>;
  resendCode(): Promise<void>;
}) {
  return (
    <Rows spacing={24}>
      <Rows spacing={16}>
        {!hideTitle && <Title1>Verify your email</Title1>}
        <TextRegularLight>
          We sent you a six digit confirmation code to {email}. Please enter it
          below to confirm your email address.
        </TextRegularLight>
      </Rows>
      <CodeEntryForm handleVerify={handleVerify} />
      <TextRegularLight>
        <ResendCodeInline resendCode={resendCode} />
      </TextRegularLight>
    </Rows>
  );
}

export function CodeEntryForm({
  handleVerify,
}: {
  handleVerify(code: string): Promise<void>;
}) {
  const [code, setCode] = React.useState('');
  const [loading, error, handleSubmit] = useSubmit((newCode?: string) =>
    handleVerify(newCode ?? code),
  );

  return (
    <InvisibleForm onSubmit={handleSubmit} loading={loading}>
      <FormInput
        size="large"
        type="text"
        required
        value={code}
        onChange={(v) => {
          v = v.replace(/[^0-9]/g, '');
          setCode(v);
          if (v.length === 6) handleSubmit(v);
        }}
        placeholder="Enter 6-digit code"
        pattern="[0-9]{6}"
      />
      {loading && (
        <TextSmallLight color="grey">Verifying code...</TextSmallLight>
      )}
      <RedErrorText error={error} />
    </InvisibleForm>
  );
}

function ResendCodeInline({ resendCode }: { resendCode: () => Promise<void> }) {
  const [done, setDone] = React.useState(false);
  const [loading, error, handleSubmit] = useSubmit(async () => {
    await resendCode();
    setDone(true);
  });

  if (done) {
    return <>A new code was sent - check your inbox.</>;
  }
  if (loading) {
    return <>Resending verification code...</>;
  }
  return (
    <>
      Didn't receive a code?{' '}
      <Link onClick={handleSubmit}>Send code again.</Link>
      <RedErrorText error={error} />
    </>
  );
}

export const ForgotPasswordForm = observer(function _ForgotPasswordForm({
  formState,
  goToLogin,
  submitForgotPasswordForm,
}: {
  formState: SharedFormState;
  goToLogin(): void;
  submitForgotPasswordForm(): Promise<void>;
}) {
  const [loading, error, handleSubmit] = useSubmit(submitForgotPasswordForm);

  return (
    <Rows spacing={24}>
      <Rows spacing={16}>
        <Title1>Forgot your password?</Title1>
        <TextRegularLight>
          Enter your email address below and we'll send you a code to log in and
          reset your password.
        </TextRegularLight>
      </Rows>
      <InvisibleForm onSubmit={handleSubmit} loading={loading}>
        <Email formState={formState} />
        <Button
          label={loading ? 'Loading...' : 'Send code'}
          type="submit"
          variant="primary"
          size="large"
        />
        <RedErrorText error={error} />
      </InvisibleForm>
      <DividerLine />
      <Inline yalign="center" spacing={8}>
        <TextRegularLight>Remember your password?</TextRegularLight>
        <SpacerGrow />
        <Button label="Log in" onClick={goToLogin} size="large" />
      </Inline>
    </Rows>
  );
});

export const ChangePasswordForm = observer(function _ChangePasswordForm({
  formState,
  submitChangePasswordForm,
  skipChangePassword,
}: {
  formState: SharedFormState;
  submitChangePasswordForm(): Promise<void>;
  skipChangePassword(): void;
}) {
  const [confirmPassword, setConfirmPassword] = React.useState('');
  let disabledReason: string | undefined = undefined;
  if (formState.password.length < 8) {
    disabledReason = 'Password must be at least 8 characters';
  } else if (formState.password !== confirmPassword) {
    disabledReason = 'Passwords do not match';
  }

  const [loading, error, handleSubmit] = useSubmit(async () => {
    if (disabledReason) throw new Error(disabledReason);
    await submitChangePasswordForm();
  });

  return (
    <Rows spacing={24}>
      <Rows spacing={16}>
        <Title1>Change your password</Title1>
        <TextRegularLight>
          Choose a new password for your account.{' '}
        </TextRegularLight>
      </Rows>
      <InvisibleForm onSubmit={handleSubmit} loading={loading}>
        <Rows spacing={16}>
          <Rows spacing={8}>
            <TextSmallLight color="grey">Password</TextSmallLight>
            <FormInput
              size="large"
              type="password"
              autoComplete="new-password"
              required
              value={formState.password}
              onChange={(v) => (formState.password = v)}
              placeholder="New Password"
            />
          </Rows>
          <Rows spacing={8}>
            <TextSmallLight color="grey">Confirm Password</TextSmallLight>
            <FormInput
              size="large"
              type="password"
              autoComplete="new-password"
              required
              value={confirmPassword}
              onChange={setConfirmPassword}
              placeholder="Confirm New Password"
            />
          </Rows>
        </Rows>
        <Button
          label="Change Password"
          variant="primary"
          type="submit"
          size="large"
          loading={loading}
          disabled={disabledReason != null}
          tooltip={disabledReason}
        />
        <RedErrorText error={error} />
      </InvisibleForm>
      <Button
        label="Skip"
        onClick={() => skipChangePassword()}
        size="large"
        variant="subtle"
      />
    </Rows>
  );
});

function SignupLegal() {
  return (
    <TextTinyLight color="grey">
      By clicking "Create account" above, you acknowledge that you will receive
      updates from the Relume team and that you have read, understood, and
      agreed to Relume Library's{' '}
      <Link href="https://www.relume.io/legal/terms">Terms & Conditions</Link>,{' '}
      <Link href="https://www.relume.io/legal/licensing-agreement">
        Licensing Agreement
      </Link>{' '}
      and{' '}
      <Link href="https://www.relume.io/legal/privacy-policy">
        Privacy Policy.
      </Link>
    </TextTinyLight>
  );
}

export function AuthFlowInviteTeamForm({
  accountsService,
  toastQueue,
  onDone,
}: {
  accountsService: AccountsService;
  toastQueue: ToastQueue;
  onDone: () => void;
}) {
  const [inviteLinkInfo, setInviteLinkInfo] = React.useState<{
    enabled: boolean;
    invitationLink: string | null;
  }>({ enabled: false, invitationLink: null });

  useEffect(() => {
    accountsService
      .teamGetInvitationLinkInfo()
      .then(setInviteLinkInfo)
      .catch((error) => console.error('Failed to fetch invite link:', error));
  }, [accountsService]);

  const handleCopyLink = async () => {
    try {
      if (!inviteLinkInfo.invitationLink) {
        const response = await accountsService.teamGetOrCreateInvitationLink();
        await navigator.clipboard.writeText(String(response.invitationLink));
        setInviteLinkInfo({
          enabled: true,
          invitationLink: response.invitationLink,
        });
      } else {
        await navigator.clipboard.writeText(inviteLinkInfo.invitationLink);
      }
      toastQueue.push({ label: 'Invite link copied to clipboard' });
    } catch (error) {
      console.error('Failed to copy invite link:', error);
      toastQueue.push({ label: 'Failed to copy invite link' });
    }
  };

  const handleResetLink = async () => {
    try {
      const response = await accountsService.teamResetInvitationLink();
      setInviteLinkInfo({
        enabled: true,
        invitationLink: response.invitationLink,
      });
      toastQueue.push({ label: 'Invite link reset' });
    } catch (error) {
      console.error('Failed to reset invite link:', error);
      toastQueue.push({ label: 'Failed to reset invite link' });
    }
  };

  const handleToggleLinks = async (enabled: boolean) => {
    try {
      if (enabled) {
        const response = await accountsService.teamGetOrCreateInvitationLink();
        setInviteLinkInfo({
          enabled: true,
          invitationLink: response.invitationLink,
        });
      } else {
        await accountsService.teamDisableInvitationLink();
        setInviteLinkInfo({ enabled: false, invitationLink: null });
      }
    } catch (error) {
      console.error('Failed to toggle invite links:', error);
      toastQueue.push({ label: 'Failed to toggle invite links' });
    }
  };

  return (
    <Rows spacing={24}>
      <Title5>Invite other members</Title5>
      <InviteTeamForm
        invite={async (emails) => {
          await accountsService.teamInvite({ emails, role: 'member' });
        }}
        inviteLinkInfo={inviteLinkInfo}
        onCopyLink={handleCopyLink}
        onResetLink={handleResetLink}
        onToggleLinks={handleToggleLinks}
        onDone={onDone}
      />
    </Rows>
  );
}
